import { Dispatch } from '@reduxjs/toolkit';
import {
  getListings,
  getVendors,
  deleteListing,
  updatePrice } from "../../http/listing";
import { IAction } from "../actionTypes";
import {
  ICurrency,
  TListingTableRow } from "@protoplan/types";
import { TGetState } from '../store';
import { retrieveDosingsAsync, updateListingInProtocolsAsync } from '../protocol/protocol.thunks';
import { TThunkDispatch } from '../hooks';
import {
  toggleDiscounts,
  ListingsState,
  removeListing,
  updateCurrencies,
  updateListings,
  updateVendors,
  Vendor } from './listings.slice';
import { getCurrencies, getExchangeRate } from '../../http/currency';
import { calculateListingCostWithFees } from '@protoplan/calculate-costs';
import {  enforceListingTypes } from '@protoplan/types';


export const retrieveListingsAsync = (productId: number, protocolId: number) => async(
  dispatch: Dispatch<IAction<TListingTableRow[] | undefined>>) =>
{
  return getListings(productId, protocolId)
    .then(async data =>
    {
      if(!data)
        return dispatch(updateListings([]));

      const listings = await Promise.all(data.map(l => ({
        ...enforceListingTypes(l),
        // String representation of number not successfully converted to Number by enforceListingTypes
        deliveryPerListing: l.deliveryPerListing === undefined ? undefined : Number(l.deliveryPerListing) })));

      dispatch(updateListings(listings));
    });
};

export const retrieveVendorsAsync = () => async(dispatch: Dispatch<IAction<Vendor[]>>) =>
{
  return getVendors()
    .then(data => dispatch(updateVendors(data)));
};

export const retrieveCurrenciesAsync = () => async(dispatch: Dispatch<IAction<ICurrency[]>>) =>
{
  return getCurrencies()
    .then(data => dispatch(updateCurrencies(data || [])));
};

export const deleteListingAsync = (listingId: number) => async(
  dispatch: TThunkDispatch,
  getState: TGetState) =>
{
  const protocolIds = getState().protocol.protocols
    .filter(p => p.dosings?.find(d => d.listingId === listingId))
    .map(p => p.protocolId);

  dispatch(removeListing(listingId));
  deleteListing(listingId)
    .then(() => protocolIds.forEach(id => dispatch(retrieveDosingsAsync(id))));

  return getState().listings.listings || [];
};

export const updatePriceAsync = (listingId: number, price: number, protocolId: number) => async(
  dispatch: TThunkDispatch,
  getState: TGetState) =>
{
  await updatePrice(listingId, price);

  const productId = (getState().listings as ListingsState).productId;
  if(productId)
    await dispatch(retrieveListingsAsync(productId, protocolId));

  await dispatch(updateListingInProtocolsAsync(listingId));
};

export const toggleDiscountsAsync = (listingId: number, discountIds: number[]) => async(
  dispatch: TThunkDispatch, getState: TGetState) =>
{
  const listings = getState().listings.listings as TListingTableRow[];
  const listing = listings?.find(l => l.listingId === listingId);
  if(!listing)
    return;
  const discounts = listing.discounts?.filter(d => discountIds.includes(d.discountId));
  if(!discounts)
    return;

  const listingWithDiscounts = { ...listing, discounts: listing.discounts
    ?.filter(d => !discountIds.includes(d.discountId))
    ?.concat(discounts.map(d => ({ ...d, applied: !d.applied }))) };

  // Recalculate prices
  const { discountedPrice, priceWithoutFees, priceWithFees } = await calculateListingCostWithFees(
    listingWithDiscounts,
    getExchangeRate,
    true);

  dispatch(toggleDiscounts({ listingId, discountIds, priceWithoutFees, priceWithFees, discountedPrice }));
};
